/**
 * The highlight overlay is rendered OUTSIDE the shadow DOM so it can
 * visually sit on top of any page element. We use two absolutely positioned
 * divs: one for hover highlight (blue), one per selected element (green).
 */

const WIDGET_ATTR = 'data-br-widget';

let hoverHighlight: HTMLDivElement | null = null;
let selectionContainer: HTMLDivElement | null = null;

export function createOverlayElements(): void {
  // Hover highlight (blue)
  hoverHighlight = document.createElement('div');
  hoverHighlight.setAttribute(WIDGET_ATTR, '');
  Object.assign(hoverHighlight.style, {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: '2147483644',
    border: '2px solid #3b82f6',
    background: 'rgba(59, 130, 246, 0.08)',
    borderRadius: '2px',
    transition: 'all 0.08s ease-out',
    display: 'none',
  });
  document.body.appendChild(hoverHighlight);

  // Container for selection outlines (green)
  selectionContainer = document.createElement('div');
  selectionContainer.setAttribute(WIDGET_ATTR, '');
  selectionContainer.id = 'br-selection-outlines';
  Object.assign(selectionContainer.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '0',
    height: '0',
    pointerEvents: 'none',
    zIndex: '2147483644',
  });
  document.body.appendChild(selectionContainer);
}

export function showHoverHighlight(rect: DOMRect): void {
  if (!hoverHighlight) return;
  hoverHighlight.style.display = 'block';
  hoverHighlight.style.top = `${rect.top}px`;
  hoverHighlight.style.left = `${rect.left}px`;
  hoverHighlight.style.width = `${rect.width}px`;
  hoverHighlight.style.height = `${rect.height}px`;
}

export function hideHoverHighlight(): void {
  if (!hoverHighlight) return;
  hoverHighlight.style.display = 'none';
}

export function addSelectionOutline(element: HTMLElement): void {
  if (!selectionContainer) return;
  const rect = element.getBoundingClientRect();
  const outline = document.createElement('div');
  outline.setAttribute(WIDGET_ATTR, '');
  outline.setAttribute('data-br-outline-for', generateElementId(element));
  Object.assign(outline.style, {
    position: 'fixed',
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    border: '2px solid #22c55e',
    background: 'rgba(34, 197, 94, 0.08)',
    borderRadius: '2px',
    pointerEvents: 'none',
  });
  selectionContainer.appendChild(outline);
}

export function removeSelectionOutline(element: HTMLElement): void {
  if (!selectionContainer) return;
  const id = generateElementId(element);
  const outline = selectionContainer.querySelector(`[data-br-outline-for="${id}"]`);
  if (outline) outline.remove();
}

export function clearAllOutlines(): void {
  if (!selectionContainer) return;
  selectionContainer.innerHTML = '';
}

export function updateSelectionOutlines(elements: HTMLElement[]): void {
  if (!selectionContainer) return;
  selectionContainer.innerHTML = '';
  for (const el of elements) {
    addSelectionOutline(el);
  }
}

export function removeOverlayElements(): void {
  hoverHighlight?.remove();
  selectionContainer?.remove();
  hoverHighlight = null;
  selectionContainer = null;
}

export function isWidgetElement(el: Element | null): boolean {
  if (!el) return false;
  if (el.hasAttribute(WIDGET_ATTR)) return true;
  if (el.id === 'bl-bug-reporter') return true;
  // Walk up
  let current = el.parentElement;
  while (current) {
    if (current.hasAttribute(WIDGET_ATTR) || current.id === 'bl-bug-reporter') return true;
    current = current.parentElement;
  }
  return false;
}

// Simple element ID for matching
let idCounter = 0;
const elementIdMap = new WeakMap<HTMLElement, string>();

function generateElementId(element: HTMLElement): string {
  let id = elementIdMap.get(element);
  if (!id) {
    id = `br-el-${++idCounter}`;
    elementIdMap.set(element, id);
  }
  return id;
}
